import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAlert } from '../components/AlertProvider';
import AnonymousProfileModal from '../components/AnonymousProfileModal';

interface CommunityPost {
    id: number;
    content: string;
    content_original: string;
    original_locale: string;
    hashtagsArray: string[];
    like_count: number;
    comment_count: number;
    view_count: number;
    created_at: string;
    author: {
        id: number;
        anonymousId: string;
        country: string;
        countryFlag: string;
        interests: string[];
        mbti: string | null;
        ageRange: string;
        genderHint: string | null;
    };
    isLikedByMe: boolean;
    translationIndicator: string | null;
}

type FilterType = 'all' | 'country' | 'interest';
type SortType = 'latest' | 'popular' | 'trending';

export default function CommunityFeedPage() {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const [posts, setPosts] = useState<CommunityPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [filter, setFilter] = useState<FilterType>('all');
    const [filterValue, setFilterValue] = useState('');
    const [sort, setSort] = useState<SortType>('latest');
    const [showNewPostModal, setShowNewPostModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [offset, setOffset] = useState(0);
    const limit = 20;

    useEffect(() => {
        fetchPosts(true);
    }, [filter, filterValue, sort]);

    const fetchPosts = async (reset = false) => {
        try {
            setLoading(true);
            const currentOffset = reset ? 0 : offset;

            const data = await api.community.getPosts({
                filter,
                filterValue: filterValue || undefined,
                sort,
                limit,
                offset: currentOffset,
            });

            if (data.success) {
                if (reset) {
                    setPosts(data.posts);
                    setOffset(limit);
                } else {
                    setPosts(prev => [...prev, ...data.posts]);
                    setOffset(prev => prev + limit);
                }
                setHasMore(data.hasMore);
            }
        } catch (error: any) {
            console.error('Failed to fetch posts:', error);
            showAlert(error.message || t('community.error_fetch', 'Failed to load posts'));
        } finally {
            setLoading(false);
        }
    };

    const handleLikePost = async (postId: number) => {
        try {
            const post = posts.find(p => p.id === postId);
            if (!post) return;

            const data = post.isLikedByMe
                ? await api.community.unlikePost(postId)
                : await api.community.likePost(postId);

            if (data.success) {
                setPosts(posts.map(p =>
                    p.id === postId
                        ? { ...p, like_count: data.likeCount, isLikedByMe: data.isLiked }
                        : p
                ));
            }
        } catch (error: any) {
            console.error('Failed to like post:', error);
            showAlert(error.message || t('community.error_like', 'Failed to like post'));
        }
    };

    const handleNewPost = async (content: string, hashtags: string[]) => {
        try {
            const data = await api.community.createPost(content, hashtags);

            if (data.success) {
                showAlert(t('community.post_created', 'Post created successfully!'));
                setShowNewPostModal(false);
                fetchPosts(true); // Refresh feed
                return { success: true, remainingQuota: data.remainingQuota };
            } else {
                showAlert(data.error || t('community.error_create', 'Failed to create post'));
                return { success: false, error: data.error };
            }
        } catch (error: any) {
            console.error('Failed to create post:', error);
            showAlert(error.message || t('community.error_create', 'Failed to create post'));
            return { success: false, error: error.message };
        }
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gradient-premium">
                            {t('community.title', 'Community Feed')}
                        </h1>
                        <p className="text-sm text-base-content/60 mt-1">
                            {t('community.subtitle', 'Connect with people from around the world')}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowNewPostModal(true)}
                        className="btn-premium flex items-center gap-2 py-2.5 px-5"
                    >
                        <span className="text-lg">✏️</span>
                        <span className="hidden sm:inline">{t('community.new_post', 'New Post')}</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="glass-card p-4 mb-6">
                    <div className="flex flex-wrap gap-3">

                        {/* Filters */}
                        <div className="flex gap-2 flex-wrap">
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value as SortType)}
                                className="select select-bordered select-sm rounded-xl bg-white/80"
                            >
                                <option value="latest">{t('community.sort_latest', '🕐 Latest')}</option>
                                <option value="popular">{t('community.sort_popular', '🔥 Popular')}</option>
                                <option value="trending">{t('community.sort_trending', '📈 Trending')}</option>
                            </select>

                            <select
                                value={filter}
                                onChange={(e) => {
                                    setFilter(e.target.value as FilterType);
                                    setFilterValue('');
                                }}
                                className="select select-bordered select-sm rounded-xl bg-white/80"
                            >
                                <option value="all">{t('community.filter_all', '🌍 All Countries')}</option>
                                <option value="country">{t('community.filter_country', '🏳️ By Country')}</option>
                                <option value="interest">{t('community.filter_interest', '💡 By Interest')}</option>
                            </select>

                            {/* Filter Value */}
                            {filter === 'country' && (
                                <select
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                    className="select select-bordered select-sm rounded-xl bg-white/80"
                                >
                                    <option value="">Select Country</option>
                                    <option value="KR">🇰🇷 Korea</option>
                                    <option value="JP">🇯🇵 Japan</option>
                                    <option value="US">🇺🇸 USA</option>
                                    <option value="CN">🇨🇳 China</option>
                                    <option value="TW">🇹🇼 Taiwan</option>
                                </select>
                            )}

                            {filter === 'interest' && (
                                <input
                                    type="text"
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                    placeholder={t('community.filter_interest_placeholder', 'e.g. travel, music')}
                                    className="input input-bordered input-sm rounded-xl bg-white/80"
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Posts Feed */}
                <div className="space-y-4">
                    {loading && posts.length === 0 ? (
                        <LoadingState />
                    ) : posts.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <>
                            {posts.map((post, index) => (
                                <div key={post.id} className="fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
                                    <PostCard
                                        post={post}
                                        onLike={handleLikePost}
                                        onCommentClick={() => navigate(`/community/${post.id}`)}
                                        onProfileClick={() => setSelectedUserId(post.author.id)}
                                    />
                                </div>
                            ))}

                            {/* Load More */}
                            {hasMore && (
                                <div className="flex justify-center py-6">
                                    <button
                                        onClick={() => fetchPosts(false)}
                                        disabled={loading}
                                        className="btn btn-outline btn-primary rounded-full px-8"
                                    >
                                        {loading ? t('community.loading', 'Loading...') : t('community.load_more', 'Load More')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* New Post Modal */}
            {showNewPostModal && (
                <NewPostModal
                    onClose={() => setShowNewPostModal(false)}
                    onSubmit={handleNewPost}
                />
            )}

            {/* Profile Modal */}
            {selectedUserId && (
                <AnonymousProfileModal
                    userId={selectedUserId}
                    onClose={() => setSelectedUserId(null)}
                />
            )}
        </Layout>
    );
}

// ============================================
// PostCard Component
// ============================================
interface PostCardProps {
    post: CommunityPost;
    onLike: (postId: number) => void;
    onCommentClick: () => void;
    onProfileClick: () => void;
}

function PostCard({ post, onLike, onCommentClick, onProfileClick }: PostCardProps) {
    const { t } = useTranslation();
    const [showOriginal, setShowOriginal] = useState(false);

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return t('time.just_now', 'Just now');
        if (seconds < 3600) return t('time.minutes_ago', '{{count}}m', { count: Math.floor(seconds / 60) });
        if (seconds < 86400) return t('time.hours_ago', '{{count}}h', { count: Math.floor(seconds / 3600) });
        return t('time.days_ago', '{{count}}d', { count: Math.floor(seconds / 86400) });
    };

    return (
        <div className="premium-card p-5 hover-lift">
            {/* Author Info */}
            <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={onProfileClick}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-coral-300 to-mint-300 flex items-center justify-center text-xl shadow-md">
                    {post.author.countryFlag}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-base-content hover:text-primary transition-colors truncate">
                            {post.author.anonymousId}
                        </span>
                        <span className="text-xs text-base-content/40">•</span>
                        <span className="text-xs text-base-content/50">{formatTimeAgo(post.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {post.author.mbti && (
                            <span className="badge badge-sm bg-primary/10 text-primary border-0">{post.author.mbti}</span>
                        )}
                        {post.author.ageRange && (
                            <span className="text-xs text-base-content/50">{post.author.ageRange}</span>
                        )}
                        {post.author.interests.slice(0, 2).map((interest, i) => (
                            <span key={i} className="text-xs text-base-content/40">#{interest}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="mb-4 cursor-pointer" onClick={onCommentClick}>
                <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                    {showOriginal ? post.content_original : post.content}
                </p>

                {/* Translation Indicator */}
                {post.translationIndicator && (
                    <div className="mt-2 text-xs text-base-content/50 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <span>{post.translationIndicator}</span>
                        <button
                            onClick={() => setShowOriginal(!showOriginal)}
                            className="text-primary hover:underline"
                        >
                            {showOriginal ? t('community.show_translation', 'Show Translation') : t('community.show_original', 'Show Original')}
                        </button>
                    </div>
                )}

                {/* Hashtags */}
                {post.hashtagsArray.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" onClick={e => e.stopPropagation()}>
                        {post.hashtagsArray.map((tag, i) => (
                            <span key={i} className="text-xs text-primary hover:underline cursor-pointer">
                                #{tag}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-5 pt-3 border-t border-base-200">
                <button
                    onClick={() => onLike(post.id)}
                    className={`flex items-center gap-1.5 transition-all ${post.isLikedByMe ? 'text-error scale-105' : 'text-base-content/50 hover:text-error'}`}
                >
                    <span className="text-lg">{post.isLikedByMe ? '❤️' : '🤍'}</span>
                    <span className="text-sm font-medium">{post.like_count}</span>
                </button>

                <button
                    onClick={onCommentClick}
                    className="flex items-center gap-1.5 text-base-content/50 hover:text-primary transition-colors"
                >
                    <span className="text-lg">💬</span>
                    <span className="text-sm font-medium">{post.comment_count}</span>
                </button>

                <button className="flex items-center gap-1.5 text-base-content/50 hover:text-secondary transition-colors">
                    <span className="text-lg">👁️</span>
                    <span className="text-sm font-medium">{post.view_count}</span>
                </button>
            </div>
        </div>
    );
}

// ============================================
// NewPostModal Component
// ============================================
interface NewPostModalProps {
    onClose: () => void;
    onSubmit: (content: string, hashtags: string[]) => Promise<{ success: boolean; remainingQuota?: number; error?: string }>;
}

function NewPostModal({ onClose, onSubmit }: NewPostModalProps) {
    const { t } = useTranslation();
    const [content, setContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const maxLength = 500;

    const handleSubmit = async () => {
        if (content.trim().length === 0) return;

        setSubmitting(true);
        const result = await onSubmit(content, []);
        setSubmitting(false);

        if (result.success) {
            setContent('');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">{t('community.create_post', 'Create New Post')}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        ✕
                    </button>
                </div>

                <div className="mb-4">
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value.slice(0, maxLength))}
                        placeholder={t('community.post_placeholder', "What's on your mind?")}
                        className="w-full h-40 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none resize-none"
                        disabled={submitting}
                    />
                    <div className="flex justify-between items-center mt-2">
                        <span className="text-sm text-gray-500">
                            {content.length} / {maxLength}
                        </span>
                        <span className="text-sm text-gray-400">
                            ⏱️ {t('community.auto_delete', 'Auto-deletes in 7 days')}
                        </span>
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                        disabled={submitting}
                    >
                        {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={content.trim().length === 0 || submitting}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? t('common.posting', 'Posting...') : t('common.post', 'Post')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============================================
// Loading & Empty States
// ============================================
function LoadingState() {
    return (
        <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
    );
}

function EmptyState() {
    const { t } = useTranslation();
    return (
        <div className="text-center py-20">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {t('community.no_posts', 'No posts yet')}
            </h3>
            <p className="text-gray-500">
                {t('community.be_first', 'Be the first to share something!')}
            </p>
        </div>
    );
}
